/**
 * Anchored popover portal.
 *
 * Positions a panel relative to an anchor element using position:fixed, with automatic
 * flipping to fit the viewport. Closes on outside clicks and Escape key.
 */

import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTheme, spacing, shadows } from '@fti/design-system';
import type { PopoverProps } from './contracts';

export function Popover({ anchor, open, onClose, children, placement = 'bottom-start', width = 320 }: PopoverProps) {
  const { theme } = useTheme();
  const panelRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<{ top: number; left: number; flip: boolean }>({ top: 0, left: 0, flip: false });

  // Compute position when anchor or panel size changes
  useEffect(() => {
    if (!open || !anchor || !panelRef.current) return;

    const updatePosition = () => {
      const anchorRect = anchor.getBoundingClientRect();
      const panelRect = panelRef.current!.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;
      const margin = 8;
      const maxHeight = Math.min(viewportHeight * 0.7, 520);

      // Default: place below anchor
      let top = anchorRect.bottom + 4;
      let left = placement === 'bottom-start' ? anchorRect.left : anchorRect.right - width;
      let flip = false;

      // Flip above if not enough room below
      const spaceBelow = viewportHeight - anchorRect.bottom;
      const spaceAbove = anchorRect.top;
      if (spaceBelow < maxHeight && spaceAbove > spaceBelow) {
        top = anchorRect.top - Math.min(panelRect.height, maxHeight) - 4;
        flip = true;
      }

      // Clamp horizontally into viewport
      left = Math.max(margin, Math.min(left, viewportWidth - width - margin));

      setPosition({ top, left, flip });
    };

    updatePosition();

    window.addEventListener('resize', updatePosition);
    // Close on scroll of ancestors (simpler than tracking scroll position)
    window.addEventListener('scroll', onClose, true);

    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', onClose, true);
    };
  }, [open, anchor, placement, width, onClose]);

  // Focus management
  useEffect(() => {
    if (!open || !panelRef.current) return;

    const panel = panelRef.current;
    const previousFocus = document.activeElement as HTMLElement;

    // Move focus to first focusable element in panel
    const focusable = panel.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    if (focusable.length > 0) {
      focusable[0].focus();
    } else {
      panel.focus();
    }

    return () => {
      // Restore focus to anchor on close
      if (previousFocus && anchor && anchor.contains(previousFocus)) {
        previousFocus.focus();
      } else if (anchor && anchor instanceof HTMLElement) {
        anchor.focus();
      }
    };
  }, [open, anchor]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, onClose]);

  // Close on outside mousedown
  useEffect(() => {
    if (!open) return;

    const handleMouseDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        panelRef.current &&
        !panelRef.current.contains(target) &&
        anchor &&
        !anchor.contains(target)
      ) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [open, anchor, onClose]);

  if (!open || !anchor) return null;

  const panelStyle: React.CSSProperties = {
    position: 'fixed',
    top: position.top,
    left: position.left,
    width,
    maxHeight: Math.min(window.innerHeight * 0.7, 520),
    overflowY: 'auto',
    background: theme.colors.background.elevated,
    border: `1px solid ${theme.colors.border.default}`,
    borderRadius: '8px',
    boxShadow: shadows.lg,
    padding: spacing.sm,
    zIndex: 1000,
  };

  return createPortal(
    <div ref={panelRef} role="dialog" style={panelStyle} tabIndex={-1}>
      {children}
    </div>,
    document.body
  );
}
